import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { FiCheckCircle, FiHeadphones, FiInfo, FiMonitor, FiPaperclip, FiSend, FiUser } from "react-icons/fi";
import BreadCrumbs from "../../components/BreadCrumbs/BreadCrumbs";
import { useCustomer } from "../../Contexts/CustomerContext";
import { useEnvironment } from "../../Contexts/EnvironmentContext";
import {
  CreateSupportRequestPayload,
  SupportAttachmentPayload,
  SupportRequestAttachment,
  SupportRequestPriority,
  SupportRequestListItem,
  SupportRequestType,
} from "../../interfaces";
import {
  closeSupportRequest,
  downloadSupportAttachment,
  createSupportRequest,
  getSupportRequests,
} from "../../services/support.service";
import AttachmentDropzone, { formatFileSize } from "./AttachmentDropzone";
import styles from "./Support.module.scss";

interface SupportFormState {
  requestType: SupportRequestType | "";
  priority: SupportRequestPriority;
  subject: string;
  description: string;
}

const initialState: SupportFormState = {
  requestType: "",
  priority: "medium",
  subject: "",
  description: "",
};

const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_FILE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".pdf", ".log", ".txt"];
const ATTACHMENT_HINT = `PNG, JPG, PDF, LOG, TXT · Max ${
  MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024)
} MB each · Up to ${MAX_ATTACHMENTS} files`;

type SupportView = "form" | "requests";
type RequestStatusFilter = "all" | "open" | "resolved";

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error(`Unable to read file content for ${file.name}.`));
        return;
      }

      const base64Part = result.split(",")[1];
      if (!base64Part) {
        reject(new Error(`Unable to parse attachment content for ${file.name}.`));
        return;
      }

      resolve(base64Part);
    };

    reader.onerror = () => {
      reject(new Error(`Unable to read file ${file.name}.`));
    };

    reader.readAsDataURL(file);
  });

const buildAttachmentsPayload = async (files: File[]): Promise<SupportAttachmentPayload[]> =>
  Promise.all(
    files.map(async (file) => ({
      file_name: file.name,
      mime_type: file.type || "application/octet-stream",
      size_bytes: file.size,
      content_base64: await readFileAsBase64(file),
    }))
  );

const SupportPage = () => {
  const [formData, setFormData] = useState<SupportFormState>(initialState);
  const [errors, setErrors] = useState<Record<keyof SupportFormState, string>>({
    requestType: "",
    priority: "",
    subject: "",
    description: "",
  });
  const [selectedAttachments, setSelectedAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [activeView, setActiveView] = useState<SupportView>("form");
  const [supportRequests, setSupportRequests] = useState<SupportRequestListItem[]>(
    []
  );
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [requestsError, setRequestsError] = useState("");
  const [closingRequestId, setClosingRequestId] = useState<number | null>(null);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<number | null>(null);
  const [requestStatusFilter, setRequestStatusFilter] = useState<RequestStatusFilter>("all");
  const [workspaceFilter, setWorkspaceFilter] = useState("all");

  const { activeCustomer, userDetails } = useCustomer();
  const { activeEnvironmentId, environments } = useEnvironment();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | "">(
    activeEnvironmentId ?? ""
  );
  const supportBreadCrumbs = useMemo(
    () => [
      {
        name: "New request",
        url: "/support",
        isActive: activeView === "form",
        onClick: () => setActiveView("form"),
      },
      {
        name: "Your requests",
        url: "/support",
        isActive: activeView === "requests",
        onClick: () => setActiveView("requests"),
      },
    ],
    [activeView]
  );
  const activeCustomerId = activeCustomer?.customer_id;
  const reporterFullName = [userDetails?.first_name, userDetails?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  const canLoadRequests = Boolean(activeCustomerId && activeEnvironmentId);

  useEffect(() => {
    if (!environments.length) {
      setSelectedWorkspaceId("");
      return;
    }

    setSelectedWorkspaceId((currentWorkspaceId) =>
      environments.some((environment) => environment.environment_id === currentWorkspaceId)
        ? currentWorkspaceId
        : activeEnvironmentId ?? environments[0].environment_id
    );
  }, [activeEnvironmentId, environments]);

  const selectedWorkspace = environments.find(
    (environment) => environment.environment_id === selectedWorkspaceId
  );

  const supportTypeOptions = useMemo(
    () => [
      { value: "query", label: "Question" },
      { value: "bug_report", label: "Bug report" },
      { value: "system_inquiry", label: "Access or setup" },
      { value: "important_bug_fix", label: "Urgent problem" },
    ],
    []
  );

  const priorityOptions = useMemo(
    () => [
      { value: "low", label: "Low" },
      { value: "medium", label: "Medium" },
      { value: "high", label: "High" },
      { value: "critical", label: "Critical" },
    ],
    []
  );

  const loadSupportRequests = useCallback(async () => {
    if (!activeCustomerId || !activeEnvironmentId) {
      setSupportRequests([]);
      return;
    }

    setIsLoadingRequests(true);
    setRequestsError("");
    try {
      const response = await getSupportRequests(
        Number(activeCustomerId),
        Number(activeEnvironmentId)
      );
      setSupportRequests(response?.items || []);
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Could not load your requests.";
      setRequestsError(message);
    } finally {
      setIsLoadingRequests(false);
    }
  }, [activeCustomerId, activeEnvironmentId]);

  useEffect(() => {
    if (activeView === "requests") {
      loadSupportRequests();
    }
  }, [activeView, loadSupportRequests]);

  useEffect(() => {
    const routesContainer = document.querySelector(".routes-container") as HTMLElement | null;
    if (!routesContainer) {
      return undefined;
    }

    const previousRoutesOverflow = routesContainer.style.overflow;
    routesContainer.style.overflow = "hidden";

    return () => {
      routesContainer.style.overflow = previousRoutesOverflow;
    };
  }, []);

  const getPriorityPillClassName = (priority: SupportRequestPriority) => {
    if (priority === "critical") {
      return "tw-bg-rose-100 tw-text-rose-700";
    }
    if (priority === "high") {
      return "tw-bg-orange-100 tw-text-orange-700";
    }
    if (priority === "medium") {
      return "tw-bg-amber-100 tw-text-amber-700";
    }
    return "tw-bg-emerald-100 tw-text-emerald-700";
  };

  const getRequestTypeLabel = (requestType: SupportRequestType) => {
    if (requestType === "bug_report") {
      return "Bug report";
    }
    if (requestType === "system_inquiry") {
      return "Access or setup";
    }
    if (requestType === "important_bug_fix") {
      return "Urgent problem";
    }
    return "Question";
  };

  const getStatusText = (status: unknown) => {
    if (typeof status === "boolean") {
      return status ? "Open" : "Resolved";
    }

    if (typeof status === "string" || typeof status === "number") {
      return String(status);
    }

    if (status && typeof status === "object") {
      const statusRecord = status as Record<string, unknown>;
      const nestedStatus =
        statusRecord.status ??
        statusRecord.status_name ??
        statusRecord.name ??
        statusRecord.label ??
        statusRecord.value;

      if (typeof nestedStatus === "string" || typeof nestedStatus === "number") {
        return String(nestedStatus);
      }
    }

    return "";
  };

  const getStatusPillClassName = (status: unknown) => {
    const normalizedStatus = getStatusText(status).toLowerCase();
    if (normalizedStatus === "resolved" || normalizedStatus === "closed") {
      return "tw-bg-emerald-100 tw-text-emerald-700";
    }
    if (
      normalizedStatus === "open" ||
      normalizedStatus === "in_progress" ||
      normalizedStatus === "in progress"
    ) {
      return "tw-bg-blue-100 tw-text-blue-700";
    }
    return "tw-bg-amber-100 tw-text-amber-700";
  };

  const formatStatus = (status: unknown) => {
    const statusText = getStatusText(status);
    return statusText ? statusText.replace(/_/g, " ") : "-";
  };

  const workspaceOptions = useMemo(
    () => Array.from(
      new Set(
        supportRequests
          .map((request) => request.environment_name?.trim())
          .filter((workspace): workspace is string => Boolean(workspace))
      )
    ).sort((first, second) => first.localeCompare(second)),
    [supportRequests]
  );

  const visibleSupportRequests = supportRequests.filter((request) => {
      const matchesWorkspace =
        workspaceFilter === "all" || request.environment_name?.trim() === workspaceFilter;
      if (!matchesWorkspace) {
        return false;
      }

      if (requestStatusFilter === "all") {
        return true;
      }

      const normalizedStatus = getStatusText(request.status).toLowerCase();
      const isResolved = normalizedStatus === "resolved" || normalizedStatus === "closed";
      return requestStatusFilter === "resolved" ? isResolved : !isResolved;
    });

  const isRequestResolved = (request: SupportRequestListItem) => {
    const normalizedStatus = getStatusText(request.status).toLowerCase();
    return normalizedStatus === "resolved" || normalizedStatus === "closed";
  };

  const handleDownloadAttachment = async (
    request: SupportRequestListItem,
    attachment: SupportRequestAttachment
  ) => {
    setDownloadingAttachmentId(attachment.attachment_id);
    setRequestsError("");
    try {
      const response = await downloadSupportAttachment(
        request.request_id,
        attachment.attachment_id
      );
      // Binary files arrive as a Blob, text ones (log, txt) as a string.
      const blob =
        response instanceof Blob
          ? response
          : new Blob([response as string], {
              type: attachment.mime_type || "application/octet-stream",
            });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.file_name;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      setRequestsError(
        error instanceof Error
          ? error.message
          : `Could not download ${attachment.file_name}.`
      );
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const handleResolveRequest = async (request: SupportRequestListItem) => {
    const confirmed = window.confirm(
      `Mark request #${request.request_id} as resolved? We'll email the reporter.`
    );
    if (!confirmed) {
      return;
    }

    setClosingRequestId(request.request_id);
    setRequestsError("");
    try {
      await closeSupportRequest(request.request_id);
      await loadSupportRequests();
    } catch (error: unknown) {
      setRequestsError(
        error instanceof Error ? error.message : "Could not mark the request as resolved."
      );
    } finally {
      setClosingRequestId(null);
    }
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleString();
  };

  const onInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
    setSubmitError("");
    setSubmitSuccess("");
  };

  const onSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
    setSubmitError("");
    setSubmitSuccess("");
  };

  const handleAttachmentFiles = (files: File[]) => {
    if (files.length === 0) return;

    const nextSelected = [...selectedAttachments];
    let nextAttachmentError = "";
    for (const file of files) {
      const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      if (!ACCEPTED_FILE_EXTENSIONS.includes(extension)) {
        nextAttachmentError = `"${file.name}" is not a supported file type.`;
        continue;
      }
      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        nextAttachmentError = `"${file.name}" exceeds 10 MB limit.`;
        continue;
      }
      const duplicateIndex = nextSelected.findIndex(
        (selectedFile) => selectedFile.name === file.name && selectedFile.size === file.size && selectedFile.lastModified === file.lastModified
      );
      if (duplicateIndex >= 0) continue;
      if (nextSelected.length >= MAX_ATTACHMENTS) {
        nextAttachmentError = `Maximum ${MAX_ATTACHMENTS} attachments are allowed.`;
        break;
      }
      nextSelected.push(file);
    }
    setSelectedAttachments(nextSelected);
    setAttachmentError(nextAttachmentError);
    setSubmitError("");
    setSubmitSuccess("");
  };

  const handleRemoveAttachment = (index: number) => {
    setSelectedAttachments((previous) =>
      previous.filter((_, fileIndex) => fileIndex !== index)
    );
    setAttachmentError("");
    setSubmitError("");
    setSubmitSuccess("");
  };

  const validate = () => {
    const nextErrors: Record<keyof SupportFormState, string> = {
      requestType: "",
      priority: "",
      subject: "",
      description: "",
    };

    if (!formData.requestType) {
      nextErrors.requestType = "Pick a request type.";
    }
    if (!formData.subject.trim()) {
      nextErrors.subject = "Add a short subject.";
    }
    if (!formData.description.trim()) {
      nextErrors.description = "Describe what you need help with.";
    }

    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    if (!validate()) {
      return;
    }

    if (!selectedWorkspace) {
      setSubmitError("Pick a workspace before sending the request.");
      return;
    }

    setIsSubmitting(true);
    try {
      const attachments = selectedAttachments.length
        ? await buildAttachmentsPayload(selectedAttachments)
        : undefined;

      const payload: CreateSupportRequestPayload = {
        request_type: formData.requestType as SupportRequestType,
        priority: formData.priority as SupportRequestPriority,
        subject: formData.subject.trim(),
        description: formData.description.trim(),
        source: "portal_web",
        reporter: {
          user_id: userDetails?.user_id ? Number(userDetails.user_id) : undefined,
          email: localStorage.getItem("email") || undefined,
          full_name: reporterFullName || undefined,
        },
        context: {
          customer_id: activeCustomer?.customer_id,
          customer_name: activeCustomer?.customer_name,
          environment_id: selectedWorkspace?.environment_id,
          environment_name: selectedWorkspace?.customer_environment_name
            ? String(selectedWorkspace.customer_environment_name)
            : undefined,
          current_path: window.location.pathname,
          user_agent: window.navigator.userAgent,
        },
        attachments,
      };

      const response = await createSupportRequest(payload);
      const fileCount = attachments?.length ?? 0;
      const files =
        fileCount === 0
          ? ""
          : fileCount === 1
          ? " 1 file attached."
          : ` ${fileCount} files attached.`;
      setSubmitSuccess(
        response?.request_id
          ? `Request #${response.request_id} sent. We'll reply to your email.${files}`
          : `Request sent. We'll reply to your email.${files}`
      );
      setFormData(initialState);
      setErrors({
        requestType: "",
        priority: "",
        subject: "",
        description: "",
      });
      setSelectedAttachments([]);
      setAttachmentError("");
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Could not send the request. Please try again.";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`${styles.supportPage} tw-flex tw-h-full tw-min-h-0 tw-w-full tw-flex-col tw-overflow-hidden tw-px-4 tw-py-6 lg:tw-px-8`}>
      <BreadCrumbs breadCrumbsData={supportBreadCrumbs} />

      {activeView === "form" ? (
        <>
          <header className={styles.pageHeader}>
            <div className={styles.headerIcon}><FiHeadphones size={30} /></div>
            <div><h1>Create support request</h1><p>Tell us what happened and we'll reply by email.</p></div>
          </header>
          <section className={styles.requestLayout}>
            <div className="tw-p-6 lg:tw-p-8">
              <p className="tw-text-[0.7rem] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-[#03234a]-700 ">
                This request covers
              </p>
              <h1 className="tw-mt-2 tw-text-2xl tw-font-semibold tw-text-slate-900">
                Customer Context
              </h1>
              <p className="tw-mt-2 tw-max-w-2xl tw-text-sm tw-leading-6 tw-text-slate-600">
                
              </p>

              <div className={styles.contextRows}>
                <div className={styles.contextRow}><span className={styles.contextIcon}><FiMonitor /></span><div>
                  <p className="tw-text-[0.65rem] tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-slate-500">
                    Customer
                  </p>
                  <p className="tw-mt-1 tw-text-sm tw-font-medium tw-text-slate-900">
                    {activeCustomer?.customer_name || "-"}
                  </p>
                </div></div>
                <div className={styles.contextRow}><span className={styles.contextIcon}><FiMonitor /></span><div>
                  <p className="tw-text-[0.65rem] tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-slate-500">
                    Workspace
                  </p>
                  <select
                    value={selectedWorkspaceId}
                    onChange={(event) => setSelectedWorkspaceId(Number(event.target.value))}
                    disabled={!environments.length}
                    className="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-900 focus:tw-border-blue-400 focus:tw-outline-none disabled:tw-cursor-not-allowed disabled:tw-bg-slate-100"
                  >
                    {!environments.length ? <option value="">No workspaces available</option> : null}
                    {environments.map((environment) => (
                      <option key={environment.environment_id} value={environment.environment_id}>
                        {environment.customer_environment_name}
                      </option>
                    ))}
                  </select>
                </div></div>
                <div className={styles.contextRow}><span className={styles.contextIcon}><FiUser /></span><div>
                  <p className="tw-text-[0.65rem] tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-slate-500">
                    Your name
                  </p>
                  <p className="tw-mt-1 tw-text-sm tw-font-medium tw-text-slate-900">
                    {reporterFullName || "-"}
                  </p>
                </div></div>
                <div className={styles.contextRow}><span className={styles.contextIcon}><FiUser /></span><div>
                  <p className="tw-text-[0.65rem] tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-slate-500">
                    Your email
                  </p>
                  <p className="tw-mt-1 tw-break-all tw-text-sm tw-font-medium tw-text-slate-900">
                    {localStorage.getItem("email") || "-"}
                  </p>
                </div></div>
              </div>

              <div className={styles.infoCard}><h3><FiInfo /> Before You Submit</h3><p><FiCheckCircle />Describe what happened and what you expected to happen</p><p><FiCheckCircle />Explain the business impact</p><p><FiCheckCircle />Include steps to reproduce (if any)</p><p><FiCheckCircle />Attach screenshots, logs or files that can help us troubleshoot</p></div>
              <div className={styles.nextCard}><h3>What Happens Next</h3><div className={styles.timeline}><div><b>1</b><p><strong>Sent</strong><span>We get it by email straight away.</span></p></div><div><b>2</b><p><strong>Picked up</strong><span>A WADE specialist looks into it.</span></p></div><div><b>3</b><p><strong>Answered</strong><span>We reply to your email address.</span></p></div></div></div>
              {/* <div className={styles.resources}><FiAlertCircle /><div>Need immediate help?<a href="#support-resources">View support resources&nbsp; →</a></div></div> */}

              {submitSuccess ? (
                <div className="tw-mt-5 tw-rounded-xl tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-p-3 tw-text-sm tw-font-semibold tw-text-emerald-700">
                  {submitSuccess}
                </div>
              ) : null}
              {submitError ? (
                <div className="tw-mt-5 tw-rounded-xl tw-border tw-border-rose-200 tw-bg-rose-50 tw-p-3 tw-text-sm tw-font-semibold tw-text-rose-700">
                  {submitError}
                </div>
              ) : null}
            </div>

            <div className={styles.detailsPanel}>
              <div>
                <p className="tw-text-[0.68rem] tw-font-semibold tw-uppercase tw-tracking-[0.25em] tw-text-[#03234a]-700">
                  Request details
                </p>
              </div>

              <div className={styles.formScroll} style={{maxHeight:"70vh"}}>
                <Form className="tw-flex tw-flex-col tw-gap-4" onSubmit={handleSubmit}>
                <label className="tw-flex tw-flex-col tw-gap-2">
                  <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-slate-600">
                    Support Type <span className="tw-text-rose-600">*</span>
                  </span>
                  <select
                    name="requestType"
                    value={formData.requestType}
                    onChange={onSelectChange}
                    className="tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                  >
                    <option value="">Select support type</option>
                    {supportTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.requestType ? (
                    <span className="tw-text-xs tw-font-semibold tw-text-rose-600">{errors.requestType}</span>
                  ) : null}
                </label>

                <label className="tw-flex tw-flex-col tw-gap-2">
                  <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-slate-600">
                    Priority
                  </span>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={onSelectChange}
                    className="tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-border-blue-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                  >
                    {priorityOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="tw-flex tw-flex-col tw-gap-2">
                  <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-slate-600">
                    Subject <span className="tw-text-rose-600">*</span>
                  </span>
                  <Form.Control
                    type="text"
                    name="subject"
                    maxLength={140}
                    value={formData.subject}
                    onChange={onInputChange}
                    placeholder="Short summary"
                    className="tw-h-11 tw-rounded-lg tw-border-slate-300 tw-text-sm focus:tw-border-blue-400 focus:tw-ring-blue-100"
                  />
                  {errors.subject ? <span className="tw-text-xs tw-font-semibold tw-text-rose-600">{errors.subject}</span> : null}
                </label>

                <label className="tw-flex tw-flex-col tw-gap-2">
                  <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-slate-600">
                    Description <span className="tw-text-rose-600">*</span>
                  </span>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    name="description"
                    maxLength={1500}
                    value={formData.description}
                    onChange={onInputChange}
                    placeholder="What happened, what you expected, and how it affects your work."
                    className="tw-min-h-[8rem] tw-flex-1 tw-resize-y tw-rounded-lg tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-border-blue-400 focus:tw-ring-blue-100"
                  />
                  <div className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                    {errors.description ? (
                      <span className="tw-font-semibold tw-text-rose-600">{errors.description}</span>
                    ) : (
                      <span className="tw-text-slate-500">The more detail you give, the faster we can help.</span>
                    )}
                    <span className="tw-font-medium tw-text-slate-500">{formData.description.length}/1500</span>
                  </div>
                </label>

                {/* A div, not a label: a label would also open the file dialog on every
                    click inside the drop area. */}
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <span className={`${styles.fieldLabel} tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-slate-600`}>
                    Attachments (Optional)
                  </span>
                  <AttachmentDropzone
                    files={selectedAttachments}
                    maxFiles={MAX_ATTACHMENTS}
                    accept={ACCEPTED_FILE_EXTENSIONS.join(",")}
                    hint={ATTACHMENT_HINT}
                    error={attachmentError}
                    onAddFiles={handleAttachmentFiles}
                    onRemoveFile={handleRemoveAttachment}
                  />
                </div>

                <div className={styles.submitBar}>
                  <p className={styles.associationHint}>
                    <FiPaperclip aria-hidden />
                    <span>
                      Linked automatically to{" "}
                      <strong>
                        {activeCustomer?.customer_name || "-"} /{" "}
                        {selectedWorkspace?.customer_environment_name ? String(selectedWorkspace.customer_environment_name) : "-"}
                      </strong>
                    </span>
                  </p>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="tw-ml-auto tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-[#03234a] tw-px-5 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-[#0a3f7f] disabled:tw-cursor-not-allowed disabled:tw-opacity-70"
                  >
                    <FiSend /> {isSubmitting ? "Sending..." : "Send request"}
                  </button>
                </div>
              </Form>
              </div>
            </div>
        </section>
        </>
      ) : (
        <section className="tw-mt-3 tw-min-h-0 tw-w-full tw-flex-1 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-gradient-to-br tw-from-white tw-to-slate-50 tw-shadow-[0_20px_55px_-35px_rgba(15,23,42,0.75)]">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-slate-200 tw-p-5">
            <div>
              <h2 className="tw-text-lg tw-font-semibold tw-text-slate-900">Your requests</h2>
              <p className="tw-text-sm tw-text-slate-600">
                <span className="tw-font-semibold tw-text-slate-900">{visibleSupportRequests.length}</span> in total
              </p>
            </div>
            <div className="tw-flex tw-items-center tw-gap-3">
              <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-700">
                Status
                <select
                  value={requestStatusFilter}
                  onChange={(event) => setRequestStatusFilter(event.target.value as RequestStatusFilter)}
                  className="tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 focus:tw-border-blue-400 focus:tw-outline-none"
                >
                  <option value="all">All</option>
                  <option value="open">Open</option>
                  <option value="resolved">Resolved</option>
                </select>
              </label>
              <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-700">
                Workspace
                <select
                  value={workspaceFilter}
                  onChange={(event) => setWorkspaceFilter(event.target.value)}
                  className="tw-max-w-[14rem] tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 focus:tw-border-blue-400 focus:tw-outline-none"
                >
                  <option value="all">All</option>
                  {workspaceOptions.map((workspace) => (
                    <option key={workspace} value={workspace}>{workspace}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={loadSupportRequests}
                disabled={isLoadingRequests || !canLoadRequests}
                className="tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-border-slate-400 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
              >
                {isLoadingRequests ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>

          {!canLoadRequests ? (
            <div className="tw-p-5 tw-text-sm tw-font-medium tw-text-amber-700">
              Pick a customer and workspace to see requests.
            </div>
          ) : null}
          {requestsError ? (
            <div className="tw-p-5 tw-text-sm tw-font-medium tw-text-rose-700">{requestsError}</div>
          ) : null}

          <div className="tw-p-5">
            <div className="tw-w-full tw-max-h-[70vh]">
              <div className="tw-max-h-[35rem] tw-overflow-auto tw-rounded-3xl tw-border tw-border-slate-200/80 tw-bg-[#f8f9ff] tw-shadow-[0_18px_40px_rgba(15,23,42,0.15)]">
                <table className="tw-min-w-[1400px] tw-w-full tw-border-separate tw-border-spacing-0 tw-bg-transparent tw-text-center tw-text-[0.95rem] tw-text-slate-900">
                  <thead>
                    <tr>
                      <th className="tw-sticky tw-top-0 tw-z-10 tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-[#052042] tw-to-[#0a2f63] tw-px-4 tw-py-4 tw-text-center tw-text-[0.82rem] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-border-r tw-border-b-2 tw-border-white/30 tw-border-b-[#052042]/40">ID</th>
                      <th className="tw-sticky tw-top-0 tw-z-10 tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-[#052042] tw-to-[#0a2f63] tw-px-4 tw-py-4 tw-text-center tw-text-[0.82rem] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-border-r tw-border-b-2 tw-border-white/30 tw-border-b-[#052042]/40">Workspace</th>
                      <th className="tw-sticky tw-top-0 tw-z-10 tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-[#052042] tw-to-[#0a2f63] tw-px-4 tw-py-4 tw-text-center tw-text-[0.82rem] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-border-r tw-border-b-2 tw-border-white/30 tw-border-b-[#052042]/40">Subject</th>
                      <th className="tw-sticky tw-top-0 tw-z-10 tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-[#052042] tw-to-[#0a2f63] tw-px-4 tw-py-4 tw-text-center tw-text-[0.82rem] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-border-r tw-border-b-2 tw-border-white/30 tw-border-b-[#052042]/40">Type</th>
                      <th className="tw-sticky tw-top-0 tw-z-10 tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-[#052042] tw-to-[#0a2f63] tw-px-4 tw-py-4 tw-text-center tw-text-[0.82rem] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-border-r tw-border-b-2 tw-border-white/30 tw-border-b-[#052042]/40">Priority</th>
                      <th className="tw-sticky tw-top-0 tw-z-10 tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-[#052042] tw-to-[#0a2f63] tw-px-4 tw-py-4 tw-text-center tw-text-[0.82rem] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-border-r tw-border-b-2 tw-border-white/30 tw-border-b-[#052042]/40">Status</th>
                      <th className="tw-sticky tw-top-0 tw-z-10 tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-[#052042] tw-to-[#0a2f63] tw-px-4 tw-py-4 tw-text-center tw-text-[0.82rem] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-border-r tw-border-b-2 tw-border-white/30 tw-border-b-[#052042]/40">Reported by</th>
                      <th className="tw-sticky tw-top-0 tw-z-10 tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-[#052042] tw-to-[#0a2f63] tw-px-4 tw-py-4 tw-text-center tw-text-[0.82rem] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-border-r tw-border-b-2 tw-border-white/30 tw-border-b-[#052042]/40">Source</th>
                      <th className="tw-sticky tw-top-0 tw-z-10 tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-[#052042] tw-to-[#0a2f63] tw-px-4 tw-py-4 tw-text-center tw-text-[0.82rem] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-border-r tw-border-b-2 tw-border-white/30 tw-border-b-[#052042]/40">Created</th>
                      <th className="tw-sticky tw-top-0 tw-z-10 tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-[#052042] tw-to-[#0a2f63] tw-px-4 tw-py-4 tw-text-center tw-text-[0.82rem] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-border-r tw-border-b-2 tw-border-white/30 tw-border-b-[#052042]/40">Description</th>
                      <th className="tw-sticky tw-top-0 tw-z-10 tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-[#052042] tw-to-[#0a2f63] tw-px-4 tw-py-4 tw-text-center tw-text-[0.82rem] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-border-r tw-border-b-2 tw-border-white/30 tw-border-b-[#052042]/40">Files</th>
                      <th className="tw-sticky tw-top-0 tw-z-10 tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-[#052042] tw-to-[#0a2f63] tw-px-4 tw-py-4 tw-text-center tw-text-[0.82rem] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-white tw-border-b-2 tw-border-b-[#052042]/40"><span className="tw-sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {!isLoadingRequests && visibleSupportRequests.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="tw-p-10 tw-text-center tw-text-sm tw-font-medium tw-text-slate-500">
                          No requests yet.
                        </td>
                      </tr>
                    ) : null}
                    {visibleSupportRequests.map((request) => (
                      <tr
                        key={request.request_id}
                        className="odd:tw-bg-[#f5f7ff] even:tw-bg-white hover:tw-bg-[#eef2ff] tw-transition-colors tw-duration-200"
                      >
                        <td className="tw-whitespace-nowrap tw-border-b tw-border-r tw-border-slate-200/80 tw-px-5 tw-py-4 tw-text-sm tw-font-semibold tw-text-slate-900">
                          #{request.request_id}
                        </td>
                        <td className="tw-whitespace-nowrap tw-border-b tw-border-r tw-border-slate-200/80 tw-px-5 tw-py-4 tw-text-sm tw-font-medium tw-text-slate-800">
                          {request.environment_name || "-"}
                        </td>
                        <td className="tw-max-w-[280px] tw-truncate tw-border-b tw-border-r tw-border-slate-200/80 tw-px-5 tw-py-4 tw-text-sm tw-font-medium tw-text-slate-800">
                          <div className="tw-truncate">{request.subject}</div>
                        </td>
                        <td className="tw-whitespace-nowrap tw-border-b tw-border-r tw-border-slate-200/80 tw-px-5 tw-py-4 tw-text-sm tw-font-medium tw-text-slate-800">
                          {getRequestTypeLabel(request.request_type)}
                        </td>
                        <td className="tw-whitespace-nowrap tw-border-b tw-border-r tw-border-slate-200/80 tw-px-5 tw-py-4 tw-text-sm tw-font-medium">
                          <span
                            className={`tw-inline-flex tw-rounded-full tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold ${getPriorityPillClassName(
                              request.priority
                            )}`}
                          >
                            {request.priority}
                          </span>
                        </td>
                        <td className="tw-whitespace-nowrap tw-border-b tw-border-r tw-border-slate-200/80 tw-px-5 tw-py-4 tw-text-sm tw-font-medium">
                          <span
                            className={`tw-inline-flex tw-rounded-full tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold ${getStatusPillClassName(
                              request.status
                            )}`}
                          >
                            {formatStatus(request.status)}
                          </span>
                        </td>
                        <td className="tw-whitespace-nowrap tw-border-b tw-border-r tw-border-slate-200/80 tw-px-5 tw-py-4 tw-text-sm tw-font-medium tw-text-slate-800">
                          <div>{request.reporter_full_name || "-"}</div>
                          {request.reporter_email ? (
                            <div className="tw-text-xs tw-font-normal tw-text-slate-500">{request.reporter_email}</div>
                          ) : null}
                        </td>
                        <td className="tw-whitespace-nowrap tw-border-b tw-border-r tw-border-slate-200/80 tw-px-5 tw-py-4 tw-text-sm tw-font-medium tw-text-slate-800">
                          {request.source}
                        </td>
                        <td className="tw-whitespace-nowrap tw-border-b tw-border-r tw-border-slate-200/80 tw-px-5 tw-py-4 tw-text-sm tw-font-medium tw-text-slate-800">
                          {formatDateTime(request.created_at)}
                        </td>
                        <td className="tw-min-w-[320px] tw-border-b tw-border-r tw-border-slate-200/80 tw-px-5 tw-py-4 tw-text-sm tw-font-medium tw-text-slate-800">
                          <p className="tw-max-w-[420px] tw-break-words">{request.description || "-"}</p>
                        </td>
                        <td className="tw-border-b tw-border-r tw-border-slate-200/80 tw-px-5 tw-py-4 tw-text-sm tw-font-medium">
                          {request.attachments?.length ? (
                            <div className={styles.attachmentCell}>
                              {request.attachments.map((attachment) => (
                                <button
                                  key={attachment.attachment_id}
                                  type="button"
                                  onClick={() => handleDownloadAttachment(request, attachment)}
                                  disabled={downloadingAttachmentId === attachment.attachment_id}
                                  title={`Download ${attachment.file_name} (${formatFileSize(attachment.size_bytes)})`}
                                  className={styles.attachmentChip}
                                >
                                  <FiPaperclip aria-hidden />
                                  <span className={styles.attachmentName}>{attachment.file_name}</span>
                                  <span className={styles.attachmentSize}>
                                    {downloadingAttachmentId === attachment.attachment_id
                                      ? "..."
                                      : formatFileSize(attachment.size_bytes)}
                                  </span>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span className="tw-text-slate-400">-</span>
                          )}
                        </td>
                        <td className="tw-whitespace-nowrap tw-border-b tw-border-slate-200/80 tw-px-5 tw-py-4 tw-text-sm tw-font-medium">
                          {isRequestResolved(request) ? (
                            <span className="tw-text-slate-400">-</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleResolveRequest(request)}
                              disabled={closingRequestId === request.request_id}
                              title="Mark as resolved and email the reporter"
                              aria-label={`Mark support request ${request.request_id} as resolved`}
                              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-border-blue-400 hover:tw-text-blue-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
                            >
                              <FiCheckCircle />
                              {closingRequestId === request.request_id ? "Resolving..." : "Mark resolved"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default SupportPage;
